import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  // hardcoded year we launched projects in Impact
  projectsStartYear = 2025;
  totalYearsCount = new Date().getFullYear() - this.projectsStartYear + 3;

  constructor() {
    // This is intentional
  }

  copyTextToClipboard(textToCopy) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = textToCopy;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  noBreakHyphen(text: string) {
    return text?.replace(/-/g, '‑');
  }

  scrollToElement(element: HTMLElement, offsetTop = 0): void {
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offsetTop;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  mapKeyValueOptions(list: any) {
    return Object.keys(list).map((key) => ({
      value: key,
      label: list[key],
    }));
  }

  mapNumberKeyValueOptions(list: any) {
    return Object.keys(list).map((key) => ({
      value: Number(key),
      label: list[key],
    }));
  }

  mapObjectToSelectMultipleList(list: any[], value: string, label: string) {
    return list?.map((currentList: any) =>
      currentList?.map((item: any) => ({
        value: item[value],
        label: item[label]
      }))
    ) ?? [];
  }

  mapObjectToSelect(list: any, value: string, label: string) {
    return list?.map((item: any) => ({
      value: item[value],
      label: item[label]
    })) ?? [];
  }

  mapObjectToSelectWithDescription(list: any, value: string, label: string, descriptions: string[]) {
    return list?.map((item: any) => {
      let description = '';
      descriptions.forEach((property, index) => {
        if (item[property]?.length > 0) {
          description += item[property];
          if (index !== descriptions.length - 1) {
            description += ' - ';
          }
        }
      });

      return {
        value: item[value],
        label: item[label],
        description
      };
    }) ?? [];
  }

  mapCustomersToSelect(list: any, leadStatus: any, customerStatus: any) {
    return list?.map((item: any) => {
      let description = '';
      if (item.childName1) {
        description += item.childName1 + '\n';
      }

      if (!item.isLead) {
        description += 'BP#: ' + item.bpNumber + ', ';
      }

      if (item.status) {
        const statusText = item.isLead ? leadStatus[item.status] : customerStatus[item.status];
        description += 'Status: ' + (item.status ? statusText : '');
      }

      return {
        value: item.id,
        label: item.isLead ? ('LEAD - ' + item.name) : item.name,
        description
      };
    }) ?? [];
  }


  mapStringValueObjectToSelect(list: any, value: string, label: string) {
    return list?.map((item: any) => ({
      value: item[value].toString(),
      label: item[label]
    })) ?? [];
  }

  mapObjectToSelectMultipleLabel(list: any, value: string, properties: string[]) {
    return list?.map((item: any) => {
      let label = '';
      properties.forEach((property, index) => {
        label += item[property];
        if (index !== properties.length - 1) {
          label += ' - ';
        }
      });
      return {
        value: item[value],
        label
      };
    }) ?? [];
  }

  getYearsSelect(displayAll: boolean, startYear?: number, numberOfYears?: number) {
    const yearsList = [];
    const totalYears = displayAll ? this.totalYearsCount : numberOfYears;
    const firstYear = displayAll ? this.projectsStartYear : startYear;
    for (let i = 0; i <= totalYears; i++) {
      yearsList.push(firstYear + i);
    }
    return yearsList.map((year) => ({
      value: year,
      label: year
    }));
  }

  hasAnyValue(obj: any) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key] && typeof obj[key] === 'object') {
          return this.hasAnyValue(obj[key]);
        } else if (obj[key] && obj[key] !== '') {
          return true;
        }
      }
    }
    return false;
  }

  formHasAnyValue(form: FormGroup<any>) {
    return Object.values(form.controls).some(({ value }) => !!value || value === 0);
  }

  /**
   * Parses a date string (yyyy-MM-dd) into a local Date, avoiding UTC-shift day drift.
   * @param value date string in yyyy-MM-dd format
   * @returns local Date or null if the value is missing/invalid
   */
  parseLocalDate(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  /**
   * Parses a time string (HH:mm) and applies it to the given base date.
   * @param value time string in HH:mm format
   * @param baseDate the date to apply the time to
   * @returns a new Date with the parsed time, or null if the value is missing/invalid
   */
  applyTimeToDate(value: string | undefined, baseDate: Date): Date | null {
    if (!value) {
      return null;
    }
    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }
    const result = new Date(baseDate);
    result.setHours(hours, minutes, 0, 0);
    return result;
  }

  /**
   * Formats the local calendar date of a Date as yyyy-MM-dd (no UTC conversion), the inverse of parseLocalDate.
   * @param date the date to format
   */
  toLocalDateString(date: Date): string {
    return `${date.getFullYear()}-${this.padTwoDigits(date.getMonth() + 1)}-${this.padTwoDigits(date.getDate())}`;
  }

  /**
   * Formats a Date using its local wall-clock time (no UTC conversion / offset suffix).
   * @param date the date to format
   */
  toLocalIsoString(date: Date): string {
    const timePart = [date.getHours(), date.getMinutes(), date.getSeconds()].map(this.padTwoDigits).join(':');
    return `${this.toLocalDateString(date)}T${timePart}`;
  }

  /**
   * Determines whether a date value represents the .NET DateTime.MinValue ("0001-01-01T00:00:00").
   * @param value the raw date value to check
   */
  isMinDate(value: string | Date | null | undefined): value is string | Date {
    if (!value) {
      return false;
    }
    const date = new Date(value);
    return !isNaN(date.getTime()) && date.getFullYear() <= 1;
  }

  /**
   * Decode JWT token and extract payload
   * @param token JWT token string
   * @returns Parsed JWT payload or null if invalid
   */
  decodeJWT(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }

  private padTwoDigits(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
